import { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { API } from "@/lib/api";
import { nightShiftSummary } from "@/lib/nightShift";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { DownloadSimple, Plus, Receipt, FilePdf, FileXls, PencilSimple, Trash, UploadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * The PDF and Excel "download" buttons are rendered as plain <a download> links
 * pointing directly to the backend. The browser sends the httpOnly auth cookie
 * (same-origin), the backend streams the file with
 *   Content-Disposition: attachment; filename="..."
 * and the browser triggers a native download — no blob URLs, no popups.
 *
 * Bearer-token auth is NOT used here because anchor clicks can't carry custom
 * headers. The httpOnly access_token cookie is set by /api/auth/login and is
 * automatically attached by the browser.
 */

const EMPTY_FORM = {
    technician_id: "",
    week_start: "",
    week_end: "",
    hourly_rate: "0",
    regular_hours: "0",
    overtime_hours: "0",
    extraordinary_hours: "0",
    night_shift_diff: "0",
    deductions: "0",
    total_pay: "0",
    site: "",
    notes: "",
};

export default function Reports() {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";

    const [reports, setReports] = useState([]);
    const [technicians, setTechnicians] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshTick, setRefreshTick] = useState(0);

    // Filters (admin only)
    const [filterTech, setFilterTech] = useState("all");
    const [filterStart, setFilterStart] = useState("");
    const [filterEnd, setFilterEnd] = useState("");
    const [filterSite, setFilterSite] = useState("all");
    const [sites, setSites] = useState([]);

    // Dialog
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const params = {};
                if (isAdmin) {
                    if (filterTech && filterTech !== "all") params.technician_id = filterTech;
                    if (filterStart) params.week_start = filterStart;
                    if (filterEnd) params.week_end = filterEnd;
                    if (filterSite && filterSite !== "all") params.site = filterSite;
                }
                const { data } = await api.get("/reports", { params });
                if (!cancelled) setReports(data || []);
            } catch {
                if (!cancelled) toast.error("Failed to load reports");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [isAdmin, filterTech, filterStart, filterEnd, filterSite, refreshTick]);

    useEffect(() => {
        if (!isAdmin) return;
        let cancelled = false;
        api.get("/users", { params: { role: "technician" } }).then(({ data }) => {
            if (!cancelled) setTechnicians(data || []);
        });
        return () => { cancelled = true; };
    }, [isAdmin]);

    // Load sites for admin dropdowns (filter + create/edit form).
    useEffect(() => {
        if (!isAdmin) return;
        let cancelled = false;
        api.get("/sites").then(({ data }) => {
            if (!cancelled) setSites(data || []);
        }).catch(() => { /* non-fatal */ });
        return () => { cancelled = true; };
    }, [isAdmin]);

    const reload = useCallback(() => setRefreshTick((t) => t + 1), []);

    // Active sites memoized so the dropdown doesn't refilter on every render.
    const activeSites = useMemo(() => sites.filter((s) => s.active), [sites]);
    const formSiteIsLegacy = useMemo(
        () => !!form.site && !activeSites.some((s) => s.name === form.site),
        [form.site, activeSites]
    );

    const openCreate = () => {
        setEditingId(null);
        setForm(EMPTY_FORM);
        setDialogOpen(true);
    };

    const openEdit = (report) => {
        setEditingId(report.id);
        setForm({
            technician_id: report.technician_id,
            week_start: report.week_start,
            week_end: report.week_end,
            hourly_rate: String(report.hourly_rate ?? 0),
            regular_hours: String(report.regular_hours ?? 0),
            overtime_hours: String(report.overtime_hours ?? 0),
            extraordinary_hours: String(report.extraordinary_hours ?? 0),
            night_shift_diff: String(report.night_shift_diff ?? 0),
            deductions: String(report.deductions ?? 0),
            total_pay: String(report.total_pay ?? 0),
            site: report.site || "",
            notes: report.notes || "",
        });
        setDialogOpen(true);
    };

    const handleTechnicianChange = (technicianId) => {
        const tech = technicians.find((t) => t.id === technicianId);
        const defaultRate = tech ? Number(tech.pay_rate || 0) : 0;
        setForm((f) => ({
            ...f,
            technician_id: technicianId,
            // Only auto-fill hourly_rate when it's empty/zero so admin edits aren't lost.
            hourly_rate: !f.hourly_rate || Number(f.hourly_rate) === 0 ? defaultRate.toFixed(2) : f.hourly_rate,
        }));
    };

    const autoCalcTotal = () => {
        const rate = Number(form.hourly_rate || 0);
        const reg = Number(form.regular_hours || 0);
        const ot = Number(form.overtime_hours || 0);
        const extra = Number(form.extraordinary_hours || 0);
        const night = Number(form.night_shift_diff || 0);
        const ded = Number(form.deductions || 0);
        // Extraordinary hours are paid at the regular hourly rate.
        const total = reg * rate + ot * rate * 1.5 + extra * rate + night - ded;
        setForm((f) => ({ ...f, total_pay: total.toFixed(2) }));
    };

    const handleSave = async () => {
        try {
            const payload = {
                technician_id: form.technician_id,
                week_start: form.week_start,
                week_end: form.week_end,
                hourly_rate: Number(form.hourly_rate || 0),
                regular_hours: Number(form.regular_hours || 0),
                overtime_hours: Number(form.overtime_hours || 0),
                extraordinary_hours: Number(form.extraordinary_hours || 0),
                night_shift_diff: Number(form.night_shift_diff || 0),
                deductions: Number(form.deductions || 0),
                total_pay: Number(form.total_pay || 0),
                site: form.site,
                notes: form.notes,
            };
            if (!payload.technician_id || !payload.week_start || !payload.week_end) {
                toast.error("Technician and week range are required");
                return;
            }
            if (editingId) {
                const { technician_id, ...updatePayload } = payload;
                await api.patch(`/reports/${editingId}`, updatePayload);
                toast.success("Report updated");
            } else {
                await api.post("/reports", payload);
                toast.success("Report created");
            }
            setDialogOpen(false);
            reload();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to save report");
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Delete this report? This cannot be undone.")) return;
        try {
            await api.delete(`/reports/${id}`);
            toast.success("Report deleted");
            reload();
        } catch {
            toast.error("Failed to delete");
        }
    };

    // Build a direct download URL pointing at the backend. The browser will
    // attach the httpOnly auth cookie automatically (same origin).
    const reportPdfUrl = (r, lang = "es") => `${API}/reports/${r.id}/pdf-download?lang=${lang}`;
    // CamelCase tech name + ISO dates → professional, easy to sort in HR folders.
    // Matches the Content-Disposition filename the backend sends, so the
    // server filename always wins; we use this as a defensive fallback.
    const reportPdfFilename = (r, lang = "es") => {
        const parts = String(r.technician_name || "Technician")
            .normalize("NFKD")
            .replace(/[\u0300-\u036f]/g, "")
            .match(/[A-Za-z0-9]+/g) || ["Technician"];
        const camel = parts.map((p) => p[0].toUpperCase() + p.slice(1).toLowerCase()).join("");
        const suffix = lang === "en" ? "_EN" : "";
        return `AdvanceNetworks_PaymentReport_${camel}_${r.week_start}_to_${r.week_end}${suffix}.pdf`;
    };

    // Click handler for the per-report PDF download.
    //
    // We request a one-shot signed download key (dl_key) and append it to the
    // pdf-download URL. This means the actual file fetch does NOT rely on
    // cookies or the Authorization header — it works even when the browser
    // drops cookies on top-level navigations (Emergent preview iframe,
    // third-party cookie blocking, ad/privacy extensions).
    //
    // No blob URLs, no window.open — just a real anchor click so Chrome shows
    // its native download bar and the file lands in Downloads.
    const handleReportDownload = async (r, lang = "es") => {
        try {
            const dlKeyResp = await api.post(`/reports/${r.id}/download-key`);
            const dlKey = dlKeyResp.data?.dl_key;
            if (!dlKey) {
                toast.error("Server did not return a download key. Please retry.");
                return;
            }
            const finalUrl = `${reportPdfUrl(r, lang)}&dl_key=${encodeURIComponent(dlKey)}`;

            // Detect Emergent preview iframe (or any framed context).
            // In framed contexts Chrome silently blocks downloads unless the
            // parent iframe has sandbox="... allow-downloads". Opening the URL
            // in a NEW TAB sidesteps this — the new tab is a top-level browsing
            // context, the response is Content-Disposition: attachment, and
            // Chrome downloads the file and auto-closes the popup tab.
            let inIframe = false;
            try { inIframe = window.self !== window.top; } catch { inIframe = true; }

            const link = document.createElement("a");
            link.href = finalUrl;
            link.download = reportPdfFilename(r, lang);
            link.rel = "noopener";
            if (inIframe) link.target = "_blank";
            link.style.display = "none";
            document.body.appendChild(link);
            link.click();
            setTimeout(() => link.remove(), 1500);

            // Recovery toast — gives the user a clickable fallback URL if the
            // browser/ad-blocker/iframe-sandbox swallows the download silently.
            toast.success("Download started", {
                description: "If nothing downloaded, click here to open the PDF.",
                action: {
                    label: "Open PDF",
                    onClick: () => window.open(finalUrl, "_blank", "noopener"),
                },
                duration: 8000,
            });
        } catch (err) {
            toast.error(
                err?.response?.data?.detail ||
                err?.message ||
                "Could not start download"
            );
        }
    };

    const teamPdfUrl = () => {
        const params = new URLSearchParams();
        if (filterTech && filterTech !== "all") params.set("technician_id", filterTech);
        if (filterStart) params.set("week_start", filterStart);
        if (filterEnd) params.set("week_end", filterEnd);
        const qs = params.toString();
        return `${API}/reports/export/team-pdf${qs ? `?${qs}` : ""}`;
    };
    const excelUrl = () => {
        const params = new URLSearchParams();
        if (filterTech && filterTech !== "all") params.set("technician_id", filterTech);
        if (filterStart) params.set("week_start", filterStart);
        if (filterEnd) params.set("week_end", filterEnd);
        const qs = params.toString();
        return `${API}/reports/export/excel${qs ? `?${qs}` : ""}`;
    };
    // Filename hints — defensive fallbacks. The server's Content-Disposition
    // header is the source of truth and will override these when the browser
    // honors it.
    const exportFilenameTag = () => {
        return filterStart && filterEnd ? `_${filterStart}_to_${filterEnd}` : "";
    };
    const teamPdfFilename = () => `AdvanceNetworks_TeamPayroll${exportFilenameTag()}.pdf`;
    const excelFilename = () => `AdvanceNetworks_TeamPayroll${exportFilenameTag()}.xlsx`;


    return (
        <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
                <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-1.5">
                        {isAdmin ? "Admin · Payment Reports" : "My Reports"}
                    </div>
                    <h1 className="text-3xl sm:text-4xl font-bold text-[#003366]" style={{ fontFamily: "Outfit" }}>
                        Weekly Payment Reports
                    </h1>
                    <p className="text-slate-600 mt-2 text-sm">
                        {isAdmin
                            ? "Create, edit, and export weekly payment reports for your team."
                            : "Download and review your weekly payment reports."}
                    </p>
                </div>
                {isAdmin && (
                <div className="flex flex-wrap gap-2">
                        <a
                            href={teamPdfUrl()}
                            download={teamPdfFilename()}
                            className="inline-flex items-center justify-center h-10 px-4 rounded-md border border-[#003366] text-[#003366] text-sm font-medium hover:bg-slate-50 transition-colors"
                            data-testid="export-team-pdf-button"
                        >
                            <FilePdf size={16} className="mr-1.5" /> Team PDF
                        </a>
                        <a
                            href={excelUrl()}
                            download={excelFilename()}
                            className="inline-flex items-center justify-center h-10 px-4 rounded-md border border-[#003366] text-[#003366] text-sm font-medium hover:bg-slate-50 transition-colors"
                            data-testid="export-excel-button"
                        >
                            <FileXls size={16} className="mr-1.5" /> Excel
                        </a>
                        <Link to="/admin/import-csv">
                            <Button
                                variant="outline"
                                className="h-10 border-[#003366] text-[#003366] hover:bg-slate-50"
                                data-testid="import-csv-button"
                            >
                                <UploadSimple size={16} className="mr-1.5" /> Import CSV
                            </Button>
                        </Link>
                        <Button
                            onClick={openCreate}
                            className="bg-[#0066D9] hover:bg-[#0052ad] text-white"
                            data-testid="create-report-button"
                        >
                            <Plus size={16} className="mr-1.5" /> New report
                        </Button>
                    </div>
                )}
            </div>

            {/* Filters (admin) */}
            {isAdmin && (
                <Card className="border-[#E2E8F0]">
                    <CardContent className="p-4 sm:p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                        <div>
                            <Label className="text-[10px] uppercase tracking-wider font-semibold text-[#475569]">
                                Technician
                            </Label>
                            <Select value={filterTech} onValueChange={setFilterTech}>
                                <SelectTrigger className="h-10 mt-1" data-testid="filter-technician-select">
                                    <SelectValue placeholder="All technicians" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All technicians</SelectItem>
                                    {technicians.map((t) => (
                                        <SelectItem key={t.id} value={t.id}>
                                            {t.full_name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-[10px] uppercase tracking-wider font-semibold text-[#475569]">
                                Week start (from)
                            </Label>
                            <Input
                                type="date"
                                value={filterStart}
                                onChange={(e) => setFilterStart(e.target.value)}
                                className="h-10 mt-1"
                                data-testid="filter-week-start-input"
                            />
                        </div>
                        <div>
                            <Label className="text-[10px] uppercase tracking-wider font-semibold text-[#475569]">
                                Week end (to)
                            </Label>
                            <Input
                                type="date"
                                value={filterEnd}
                                onChange={(e) => setFilterEnd(e.target.value)}
                                className="h-10 mt-1"
                                data-testid="filter-week-end-input"
                            />
                        </div>
                        <div>
                            <Label className="text-[10px] uppercase tracking-wider font-semibold text-[#475569]">
                                Site
                            </Label>
                            <Select value={filterSite} onValueChange={setFilterSite}>
                                <SelectTrigger className="h-10 mt-1" data-testid="filter-site-select">
                                    <SelectValue placeholder="All sites" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All sites</SelectItem>
                                    {sites.map((s) => (
                                        <SelectItem key={s.id} value={s.name}>
                                            {s.name}{!s.active ? " (inactive)" : ""}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Reports table */}
            <Card className="border-[#E2E8F0]">
                <CardContent className="p-0">
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow className="bg-slate-50 hover:bg-slate-50">
                                    {isAdmin && <TableHead>Technician</TableHead>}
                                    <TableHead>Week</TableHead>
                                    <TableHead className="text-right">Reg. Hrs</TableHead>
                                    <TableHead className="text-right">OT Hrs</TableHead>
                                    <TableHead className="text-right">Extra Hrs</TableHead>
                                    <TableHead className="text-right">Night Diff</TableHead>
                                    <TableHead className="text-right">Deductions</TableHead>
                                    <TableHead className="text-right">Total Pay</TableHead>
                                    <TableHead className="text-right">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={isAdmin ? 9 : 8} className="text-center py-10 text-slate-500">
                                            Loading reports...
                                        </TableCell>
                                    </TableRow>
                                ) : reports.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={isAdmin ? 9 : 8} className="text-center py-10 text-slate-500">
                                            <Receipt size={32} className="mx-auto mb-3 text-slate-300" weight="duotone" />
                                            <div>No reports found.</div>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    reports.map((r) => (
                                        <TableRow key={r.id} data-testid={`report-row-${r.id}`}>
                                            {isAdmin && (
                                                <TableCell className="font-medium text-[#1F2A44]">
                                                    {r.technician_name || "—"}
                                                </TableCell>
                                            )}
                                            <TableCell className="text-sm">
                                                {r.week_start} → {r.week_end}
                                            </TableCell>
                                            <TableCell className="text-right">{Number(r.regular_hours).toFixed(2)}</TableCell>
                                            <TableCell className="text-right">{Number(r.overtime_hours).toFixed(2)}</TableCell>
                                            <TableCell className="text-right">{Number(r.extraordinary_hours || 0).toFixed(2)}</TableCell>
                                            <TableCell className="text-right" data-testid={`report-night-diff-${r.id}`}>
                                                <div>${Number(r.night_shift_diff).toFixed(2)}</div>
                                                {nightShiftSummary(r) && (
                                                    <div className="text-[10px] text-slate-500 whitespace-nowrap" data-testid={`report-night-detail-${r.id}`}>
                                                        {nightShiftSummary(r)}
                                                    </div>
                                                )}
                                            </TableCell>
                                            <TableCell className="text-right">${Number(r.deductions).toFixed(2)}</TableCell>
                                            <TableCell className="text-right font-semibold text-[#003366]">
                                                ${Number(r.total_pay).toFixed(2)}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <div className="flex items-center justify-end gap-1.5">
                                                    <DropdownMenu>
                                                        <DropdownMenuTrigger asChild>
                                                            <button
                                                                type="button"
                                                                className="inline-flex items-center justify-center h-8 px-3 rounded-md border border-[#003366] text-[#003366] text-xs font-medium hover:bg-slate-50 transition-colors cursor-pointer"
                                                                data-testid={`download-pdf-${r.id}`}
                                                            >
                                                                <DownloadSimple size={14} className="mr-1" /> PDF
                                                            </button>
                                                        </DropdownMenuTrigger>
                                                        <DropdownMenuContent align="end" className="min-w-[10rem]">
                                                            <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-slate-500">
                                                                Report language
                                                            </DropdownMenuLabel>
                                                            <DropdownMenuItem
                                                                onSelect={() => handleReportDownload(r, "es")}
                                                                data-testid={`download-pdf-es-${r.id}`}
                                                            >
                                                                <FilePdf size={14} className="mr-2 text-[#E6002C]" /> Español
                                                            </DropdownMenuItem>
                                                            <DropdownMenuItem
                                                                onSelect={() => handleReportDownload(r, "en")}
                                                                data-testid={`download-pdf-en-${r.id}`}
                                                            >
                                                                <FilePdf size={14} className="mr-2 text-[#E6002C]" /> English
                                                            </DropdownMenuItem>
                                                        </DropdownMenuContent>
                                                    </DropdownMenu>
                                                    {isAdmin && (
                                                        <>
                                                            <Button
                                                                size="sm"
                                                                variant="ghost"
                                                                className="h-8 px-2"
                                                                onClick={() => openEdit(r)}
                                                                data-testid={`edit-report-${r.id}`}
                                                            >
                                                                <PencilSimple size={14} />
                                                            </Button>
                                                            <Button
                                                                size="sm"
                                                                variant="ghost"
                                                                className="h-8 px-2 text-[#E6002C] hover:text-[#E6002C] hover:bg-red-50"
                                                                onClick={() => handleDelete(r.id)}
                                                                data-testid={`delete-report-${r.id}`}
                                                            >
                                                                <Trash size={14} />
                                                            </Button>
                                                        </>
                                                    )}
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </CardContent>
            </Card>

            {/* Create/Edit Report Dialog */}
            {isAdmin && (
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                    <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle className="text-[#003366]" style={{ fontFamily: "Outfit" }}>
                                {editingId ? "Edit Payment Report" : "Create Payment Report"}
                            </DialogTitle>
                        </DialogHeader>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
                            <div className="sm:col-span-2 space-y-2">
                                <Label className="text-xs uppercase tracking-wider font-semibold text-[#475569]">
                                    Technician
                                </Label>
                                <Select
                                    value={form.technician_id}
                                    onValueChange={handleTechnicianChange}
                                    disabled={!!editingId}
                                >
                                    <SelectTrigger className="h-11" data-testid="report-technician-select">
                                        <SelectValue placeholder="Select technician" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {technicians.map((t) => (
                                            <SelectItem key={t.id} value={t.id}>
                                                {t.full_name} · ${Number(t.pay_rate || 0).toFixed(2)}/hr
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label className="text-xs uppercase tracking-wider font-semibold text-[#475569]">
                                    Week start
                                </Label>
                                <Input
                                    type="date"
                                    value={form.week_start}
                                    onChange={(e) => setForm((f) => ({ ...f, week_start: e.target.value }))}
                                    className="h-11"
                                    data-testid="report-week-start-input"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-xs uppercase tracking-wider font-semibold text-[#475569]">
                                    Week end
                                </Label>
                                <Input
                                    type="date"
                                    value={form.week_end}
                                    onChange={(e) => setForm((f) => ({ ...f, week_end: e.target.value }))}
                                    className="h-11"
                                    data-testid="report-week-end-input"
                                />
                            </div>
                            <div className="space-y-2 sm:col-span-2">
                                <Label className="text-xs uppercase tracking-wider font-semibold text-[#475569]">
                                    Hourly rate ($/hr) — saved on this report
                                </Label>
                                <Input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={form.hourly_rate}
                                    onChange={(e) => setForm((f) => ({ ...f, hourly_rate: e.target.value }))}
                                    placeholder="Auto-fills from technician profile"
                                    className="h-11"
                                    data-testid="report-hourly-rate-input"
                                />
                                <p className="text-xs text-slate-500">
                                    Defaults to the technician's profile pay rate. Edit to override for this report only.
                                </p>
                            </div>
                            <div className="space-y-2">
                                <Label className="text-xs uppercase tracking-wider font-semibold text-[#475569]">
                                    Regular hours
                                </Label>
                                <Input
                                    type="number"
                                    step="0.25"
                                    value={form.regular_hours}
                                    onChange={(e) => setForm((f) => ({ ...f, regular_hours: e.target.value }))}
                                    className="h-11"
                                    data-testid="report-regular-hours-input"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-xs uppercase tracking-wider font-semibold text-[#475569]">
                                    Overtime hours
                                </Label>
                                <Input
                                    type="number"
                                    step="0.25"
                                    value={form.overtime_hours}
                                    onChange={(e) => setForm((f) => ({ ...f, overtime_hours: e.target.value }))}
                                    className="h-11"
                                    data-testid="report-overtime-hours-input"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-xs uppercase tracking-wider font-semibold text-[#475569]">
                                    Extraordinary hours
                                </Label>
                                <Input
                                    type="number"
                                    step="0.25"
                                    value={form.extraordinary_hours}
                                    onChange={(e) => setForm((f) => ({ ...f, extraordinary_hours: e.target.value }))}
                                    className="h-11"
                                    data-testid="report-extraordinary-hours-input"
                                />
                                <p className="text-xs text-slate-500">
                                    Horas extraordinarias — paid at the regular hourly rate.
                                </p>
                            </div>
                            <div className="space-y-2">
                                <Label className="text-xs uppercase tracking-wider font-semibold text-[#475569]">
                                    Night shift differential ($)
                                </Label>
                                <Input
                                    type="number"
                                    step="0.01"
                                    value={form.night_shift_diff}
                                    onChange={(e) => setForm((f) => ({ ...f, night_shift_diff: e.target.value }))}
                                    className="h-11"
                                    data-testid="report-night-diff-input"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-xs uppercase tracking-wider font-semibold text-[#475569]">
                                    Deductions ($)
                                </Label>
                                <Input
                                    type="number"
                                    step="0.01"
                                    value={form.deductions}
                                    onChange={(e) => setForm((f) => ({ ...f, deductions: e.target.value }))}
                                    className="h-11"
                                    data-testid="report-deductions-input"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-xs uppercase tracking-wider font-semibold text-[#475569]">
                                    Site
                                </Label>
                                <Select
                                    value={form.site || "__none__"}
                                    onValueChange={(v) => setForm((f) => ({ ...f, site: v === "__none__" ? "" : v }))}
                                >
                                    <SelectTrigger className="h-11" data-testid="report-site-select">
                                        <SelectValue placeholder="Select site" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="__none__">— No site —</SelectItem>
                                        {activeSites.map((s) => (
                                            <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>
                                        ))}
                                        {/* Preserve current value even if it's not an active site (legacy data). */}
                                        {formSiteIsLegacy && (
                                            <SelectItem value={form.site}>
                                                {form.site} (legacy)
                                            </SelectItem>
                                        )}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2 sm:col-span-2">
                                <div className="flex items-center justify-between">
                                    <Label className="text-xs uppercase tracking-wider font-semibold text-[#475569]">
                                        Total pay ($)
                                    </Label>
                                    <Button
                                        type="button"
                                        size="sm"
                                        variant="ghost"
                                        className="h-7 text-[#0066D9] hover:bg-blue-50"
                                        onClick={autoCalcTotal}
                                        data-testid="auto-calc-total-button"
                                    >
                                        Auto-calculate
                                    </Button>
                                </div>
                                <Input
                                    type="number"
                                    step="0.01"
                                    value={form.total_pay}
                                    onChange={(e) => setForm((f) => ({ ...f, total_pay: e.target.value }))}
                                    className="h-11 font-bold text-[#003366]"
                                    data-testid="report-total-pay-input"
                                />
                            </div>
                            <div className="space-y-2 sm:col-span-2">
                                <Label className="text-xs uppercase tracking-wider font-semibold text-[#475569]">
                                    Notes
                                </Label>
                                <Textarea
                                    rows={3}
                                    value={form.notes}
                                    onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                                    placeholder="Any additional context, holidays, bonuses, etc."
                                    data-testid="report-notes-input"
                                />
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="report-cancel-button">
                                Cancel
                            </Button>
                            <Button
                                onClick={handleSave}
                                className="bg-[#0066D9] hover:bg-[#0052ad] text-white"
                                data-testid="report-save-button"
                            >
                                {editingId ? "Update report" : "Create report"}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            )}
        </div>
    );
}
